import { AutoCompleteController } from '../../../../src/components/formControllers/autoComplete';
import { useFormWithSchema } from '../../../../src/utils/functions/yupFormTypescript';
import React, { useEffect, useRef, useState } from 'react';
import { Box, Grid, IconButton } from '@mui/material';
import './index.scss';
import { useGetFilterDataForMapQuery } from '../../../services/mVPMapService';
import { AppCircularProgress } from '../../../components/common/loaders/AppCircularProgress';
import * as yup from 'yup';
import { MapSection } from './mapContainer';
import dayjs from 'dayjs';
import {
  useGetFieldDetailsQuery,
  useGetSearchResultsQuery,
} from '../../../services/mVPGraphQlService';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  AllowedDayType,
  MVPFiltersProps,
} from '../constants';
import { Button } from '../../../components';

import { InspectionRequest } from '../reports/inspectionRequest';
import { useFilterContext } from '../../../services/mVPMapService/filterContext';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import RestoreIcon from '@mui/icons-material/Restore';
import {
  createSearchParams,
  useNavigate,
} from 'react-router-dom';
import { DropDownButton } from '../../../components/dropdownButton';

import { Controller } from 'react-hook-form';
import FieldSelector from './FieldSelector';
import CommonAppBar from '../../../components/commonAppBar';

const formSchema = yup.object({
  farmNumber: yup.number(),
});

const TIME_RANGE = {
  // 1 June 2024
  START_TIME: 1717200000,
  // 31 Oct 2024
  END_TIME: Math.floor(new Date().getTime() / 1000),
};

const SECONDS_IN_A_DAY = 24 * 60 * 60;

export const TimeSeriesTiles: React.FC<MVPFiltersProps> = ({
  fieldId: fieldId,
  onBack,
}) => {
  const {
    control,
    formState: { errors },
    watch,
    setValue,
  } = useFormWithSchema(formSchema, {
    mode: 'onTouched',
  });
  const { filterValues: commonFilters } =
    useFilterContext();
  const wsId = useSelector(
    (state: RootState) => state.country.wsId
  );
  const currentTimeRef = useRef(
    Math.floor(new Date().getTime() / 1000)
  );
  const navigate = useNavigate();
  const currentTime = currentTimeRef.current;
  const [open, setOpen] = useState(false);
  const user = useSelector(
    (state: RootState) => state.authUser
  );
  const storedFieldId = localStorage.getItem('fieldId'); // Get the stored fieldId

  const storedIndex = localStorage?.getItem('filterIndex'); // Get the value from localStorage

  const storedIndexData = JSON?.parse(storedIndex);

  useEffect(() => {
    return () => {
      localStorage.removeItem('fieldId');
      localStorage.removeItem('filterIndex');
    };
  }, [location.pathname]);

  useEffect(() => {
    // Clear local storage on page refresh
    const handleBeforeUnload = () => {
      localStorage.removeItem('fieldId');
      localStorage.removeItem('filterIndex');
    };

    window.addEventListener(
      'beforeunload',
      handleBeforeUnload
    );

    return () => {
      window.removeEventListener(
        'beforeunload',
        handleBeforeUnload
      );
    };
  }, []);

  const {
    data: filtersDataObject,
    isFetching: isLoadingFilters,
  } = useGetFilterDataForMapQuery(
    {
      commonFilters,
      wsId,
      startTime:
        commonFilters?.season == 'R2025'
          ? 1757462400
          : 1743465600,
      endTime: currentTime,
      filterTypes: ['FILTER_FIELD_NUMBER'],
    },
    {
      skip: !commonFilters?.index || !commonFilters?.season,
    }
  );
  const filtersData =
    filtersDataObject?.data?.GetDataForTimeRangeWithFilters;

  let fieldNumberFilterOptions =
    filtersData?.fieldNumbers?.map((fieldNumber) => ({
      value: fieldNumber,
      label: fieldNumber.toString(),
    })) || [];

  if (
    fieldId &&
    !fieldNumberFilterOptions.some(
      (ele) => ele.value === fieldId
    )
  ) {
    fieldNumberFilterOptions = [
      ...fieldNumberFilterOptions,
      { value: fieldId, label: fieldId.toString() },
    ];
  }

  const selectedFarm = watch('farmNumber');
  const [searchTerm, setSearchTerm] = useState('');
  useEffect(() => {
    if (fieldId || storedFieldId) {
      setValue(
        'farmNumber',
        fieldId || Number(storedFieldId)
      );
    } else if (
      !fieldNumberFilterOptions?.find(
        (ele) => ele.value === selectedFarm
      )
    ) {
      setValue('farmNumber', undefined);
    }
  }, [fieldId, storedFieldId, setValue, filtersDataObject]);

  const { data, isLoading: isLoadingFieldDetails } =
    useGetFieldDetailsQuery(
      (selectedFarm
        ? {
            startTime: TIME_RANGE.START_TIME,
            endTime: TIME_RANGE.END_TIME,
            commonFilters,
            wsId,
            fieldNumbers: [selectedFarm],
          }
        : undefined) ?? skipToken
    );
  const fieldData =
    data?.data?.GetFieldDetailsForFieldNumbers
      ?.fieldDetails?.[0];
  const sowingTimeInSeconds =
    fieldData?.fieldDetails?.sowingTime?.seconds;
  const havestTimeInSeconds =
    fieldData?.fieldDetails?.harvestTime?.seconds;
  const plantationDate = sowingTimeInSeconds
    ? dayjs(sowingTimeInSeconds * 1000)
    : null;

  const allowedDays: AllowedDayType[] =
    fieldData?.fieldIndexList
      ?.map((ele) => {
        const indexTimeInSeconds = ele.indexTime?.seconds;
        if (indexTimeInSeconds && sowingTimeInSeconds) {
          const day = Math.floor(
            (indexTimeInSeconds - sowingTimeInSeconds) /
              SECONDS_IN_A_DAY
          );
          return { day, indexTime: indexTimeInSeconds };
        }
        return null; // return null if the condition is not met
      })
      .filter(
        (value): value is AllowedDayType =>
          value !== null && value?.day >= 0
      ) || []; // Fallback to an empty array if undefined

  const [isExpanded, setIsExpanded] = useState(false);

  const getPlatform = () => {
    const width = window.innerWidth;
    if (width <= 1024) return 'mobile';
    // if (width >= 768 && width <= 1024) return 'tablet';
    return 'web';
  };

  const platform = getPlatform();

  function useDebounce(value, delay = 3000) {
    const [debouncedValue, setDebouncedValue] =
      useState(value);

    useEffect(() => {
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);

      return () => {
        clearTimeout(handler);
      };
    }, [value, delay]);

    return debouncedValue;
  }

  const debouncedSearch = useDebounce(searchTerm, 3000);

  const { data: searchResult } = useGetSearchResultsQuery(
    {
      searchTerm: debouncedSearch,
      // season: 'K2025',
    },
    { skip: !debouncedSearch }
  );

  return (
    <>
      <CommonAppBar
        section={
          <>
            {platform === 'web' ? (
              <Grid
                container
                spacing={2}
                alignItems="center">
                <Grid
                  item
                  xs={6}
                  sm={6}
                  md={3}
                  lg={2}
                  className="field-grid"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '20px',
                  }}>
                  <Controller
                    name="farmNumber"
                    control={control}
                    render={() => (
                      <FieldSelector
                        onBack={onBack}
                        fieldData={fieldData}
                        searchTerm={searchTerm}
                        setSearchTerm={setSearchTerm}
                        fieldList={
                          searchResult?.data?.GetSearchResults?.fieldList?.slice(
                            0,
                            5
                          ) || []
                        }
                        selectedField={watch('farmNumber')}
                        onSelect={(field) => {
                          setValue(
                            'farmNumber',
                            field.fieldNumber
                          );
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs />

                {/* DropDownButton */}
                {user?.role !== 'FA' &&
                  platform === 'web' && (
                    <div
                      style={{ marginTop: 10, height: 40 }}>
                      <DropDownButton
                        handleInspectionRequest={() =>
                          setOpen(true)
                        }
                        disabled={!watch('farmNumber')}
                      />
                    </div>
                  )}
              </Grid>
            ) : (
              <Grid>
                <Grid
                  item
                  xs={6}
                  sm={6}
                  md={3}
                  lg={2}
                  className="field-grid">
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between', // Push items to extreme ends
                      width: '100%', // Ensure it spans the full width
                    }}>
                    <div
                      style={{
                        width: 200,
                      }}>
                      <AutoCompleteController
                        label="Select farm"
                        name="farmNumber"
                        control={control}
                        options={fieldNumberFilterOptions}
                        error={errors.farmNumber?.message}
                        disableClearable
                      />
                    </div>

                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 38, // Adjust as needed
                        height: 38, // Adjust as needed
                        border:
                          '1px solid rgba(0, 0, 0, 0.3)', // Border color
                        borderRadius: 2, // Rounded corners
                        backgroundColor: 'white', // Background color
                        padding: 3,
                        marginTop: 1,
                      }}>
                      <IconButton
                        // onClick={() => openSideSheet()}
                        disabled={!watch('farmNumber')}
                        sx={{
                          height: 28,
                          width: 28,
                        }}>
                        <RestoreIcon fontSize="large" />
                      </IconButton>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs />
              </Grid>
            )}
          </>
        }
      />
      {(isLoadingFilters || isLoadingFieldDetails) && (
        <AppCircularProgress />
      )}
      <Box>
        <div
          style={{
            marginTop: 31,
          }}>
          {/* <Grid
          container
          style={{
            marginTop: 50,
            backgroundColor: 'yellow',
          }}> */}
          {
            <MapSection
              //    key={item}
              commonFilters={
                storedIndex && storedIndexData?.index !== ''
                  ? storedIndexData
                  : commonFilters
              }
              fieldNumber={selectedFarm}
              allowedDays={allowedDays}
              sowingTimeInSeconds={sowingTimeInSeconds}
              havestTimeInSeconds={havestTimeInSeconds}
            />
          }
        </div>
        {/* </Grid> */}
        {(!selectedFarm || !plantationDate) && (
          <div className="text-center mt-2 no-data-text">
            {!selectedFarm
              ? 'Please select a farm to compare index on different days'
              : !isLoadingFieldDetails &&
                'Plantation Date Not Found'}
          </div>
        )}
        {isExpanded && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'rgba(0, 0, 0, 0.3)', // Semi-transparent grey background
              zIndex: 999, // Ensure it's above other content
            }}
            onClick={() => setIsExpanded(false)} // Clicking outside closes the menu
          />
        )}
        {/* Floating Buttons for Mobile */}
        {platform == 'mobile' && (
          <Box
            className="floating-buttons"
            // sx={{ position: 'relative', zIndex: 1000 }}
          >
            {/* "+" Button to Expand */}
            {!isExpanded && (
              <IconButton
                sx={{
                  backgroundColor: 'rgba(0, 105, 112, 1)',
                  color: 'white',
                  boxShadow: 3,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 105, 112, 1)',
                  },
                }}
                onClick={() => setIsExpanded(true)}>
                <AddIcon fontSize="medium" />
              </IconButton>
            )}

            {/* Expanded Menu */}
            {isExpanded && (
              <Box className="expanded-menu">
                {user?.role !== 'FA' && (
                  <Button
                    variant="button--primary"
                    onClick={() => setOpen(true)}
                    disabled={!watch('farmNumber')}
                    style={{
                      borderRadius: 40,
                      height: 44,
                    }}>
                    Inspection request
                  </Button>
                )}

                <Button
                  variant="button--primary"
                  onClick={() =>
                    navigate(
                      {
                        pathname: '/mvp/createReport',
                        search: createSearchParams({
                          fieldId:
                            selectedFarm?.toString() || '', // Convert number to string
                        }).toString(),
                      },
                      {
                        state: {
                          path: 'fromTimeSeries', // Keeping state
                        },
                      }
                    )
                  }
                  disabled={!watch('farmNumber')}
                  style={{
                    borderRadius: 40,

                    height: 44,
                  }}>
                  Field report
                </Button>

                <IconButton
                  sx={{
                    backgroundColor: 'rgba(0, 105, 112, 1)',
                    color: 'white',
                    boxShadow: 3,
                    '&:hover': {
                      backgroundColor:
                        'rgba(0, 105, 112, 1)',
                    },
                  }}
                  onClick={() => setIsExpanded(false)}>
                  <CloseIcon fontSize="medium" />
                </IconButton>
              </Box>
            )}
          </Box>
        )}
      </Box>

      <InspectionRequest
        open={open}
        setOpen={setOpen}
        seletedIds={[selectedFarm ?? 0]}
        fromDataTable={false}
      />
    </>
  );
};
